import React, { useState, useEffect } from "react";
import ExportCSVModal from "../Modals/ExportCSVModal";
import ImportCSVModal from "../Modals/ImportCSVModal";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronUpDownIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import {
  capitalizeFirstLetter,
  classNames,
  formatInput,
  waitForCookie,
} from "../../utils/helperFuctions";
import { config } from "../../utils/config";
import { getCookie } from "../../utils/cookieHandler";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import PaginationButtons from "./PaginationButtons";
import SearchableDropdownOptions from "./SearchableDropdownOptions";
import InputDateTimePickerMUI from "../InputFields/InputDatePicker/InputDateTimePickerMUI";
import SequenceConfirmationPopup from "../BillingEvents/SequenceConfirmationPopup";


/**
 * @typedef {Object} BulkActionConfig
 * @property {boolean} enabled - Whether bulk actions are enabled
 * @property {string} entityName - Name of the entity (e.g., "company", "customer")
 * @property {string} nameField - Field to use for display name (e.g., "Name", "name")
 * @property {string} buttonLabel - Label for the action button (e.g., "Bill")
 * @property {string} buttonIcon - Icon type: "bill" | "delete" | "archive" | "export"
 * @property {string} popupTitle - Title for the confirmation popup
 * @property {string} popupDescription - Description text for the popup (optional)
 * @property {boolean} showDatePicker - Whether to show date picker in popup
 * @property {string} dateLabel - Label for date picker (optional)
 * @property {string} noteText - Note text for the popup (optional)
 * @property {function} onAction - Async handler called with { selectedItems, businessDateTime? }
 */

export default function DynamicList({
  columns,
  data,
  deleteClicked,
  title,
  editClick,
  createRoles,
  duplicateClicked,
  deleteRoles,
  editRoles,
  apiRoute,
  tableNameInDb,
  bulkActions = null, // BulkActionConfig object
  callPopUp, // Callback for showing notifications
  hasImport = false, // Enable import button
  importConfig = null, // Import configuration { relationshipMappings: [] }
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number(searchParams.get("page")) || 1;
  const sDirection = searchParams.get("sortDirection");
  const sortBy = searchParams.get("sortBy");
  const q = searchParams.get("q");
  const activeFiltersQuery = searchParams.get("activeFilters");
  const location = useLocation();

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [sortDirection, setSortDirection] = useState(sDirection || "asc");
  const [sortedField, setSortedField] = useState(sortBy || null);
  const [totalCount, setTotalCount] = useState(data.totalCount);
  const [allData, setAllData] = useState(data.data);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(q || "");
  const [page, setPage] = useState(currentPage);
  const [divHeight, setDivHeight] = useState(0);
  const [filterBy, setFilterBy] = useState({ name: "", value: "" });
  const [filterValue, setFilterValue] = useState("");

  const [statusOptions, setStatusOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [dateComparisonOperator, setDateComparisonOperator] = useState("_gte");
  const [activeFilters, setActiveFilters] = useState(
    activeFiltersQuery != null ? JSON.parse(activeFiltersQuery) : []
  );
  const [filterOperator, setFilterOperator] = useState("_and");

  // Bulk action state (generic)
  const bulkActionsEnabled = bulkActions?.enabled === true;
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkActionPopup, setShowBulkActionPopup] = useState(false);
  const [bulkActionProcessing, setBulkActionProcessing] = useState(false);

  const navigate = useNavigate();
  const currentRole = getCookie("_r");

  const fetchData = async ({
    pageOverride,
    sortByOverride,
    sortDirectionOverride,
    searchOverride,
    filterByOverride,
    filterValueOverride,
    filtersOverride, // <- NEW
    isRelation,
    sortingRelationSubValue,
    sortingRelationWhereField,
    sortingRelationWhereValue,
  } = {}) => {
    try {
      setIsLoading(true);
      const _r = await waitForCookie("_r");

      const queryParams = new URLSearchParams();
      queryParams.set("currentRole", _r);
      queryParams.set("page", pageOverride ?? page);

      const effectiveSortBy = sortByOverride ?? sortedField;
      const effectiveSortDirection = sortDirectionOverride ?? sortDirection;

      if (effectiveSortBy) {
        queryParams.set("sortBy", effectiveSortBy);
        queryParams.set("sortDirection", effectiveSortDirection);
      }

      if (searchOverride !== undefined ? searchOverride : searchTerm) {
        queryParams.set("q", searchOverride ?? searchTerm);
      }

      const filtersToUse = filtersOverride ?? activeFilters;

      if (filtersToUse.length > 0) {
        queryParams.set("activeFilters", JSON.stringify(filtersToUse));
        queryParams.set("filterOperator", filterOperator);
      }
      if (isRelation === true) {
        queryParams.set("isRelation", isRelation);
      }
      if (sortingRelationSubValue != null) {
        queryParams.set("sortingRelationSubValue", sortingRelationSubValue);
      }
      if (
        sortingRelationWhereField != null &&
        sortingRelationWhereValue != null
      ) {
        queryParams.set("sortingRelationWhereField", sortingRelationWhereField);
        queryParams.set("sortingRelationWhereValue", sortingRelationWhereValue);
      }
      const resp = await axios.get(
        `${config.apiUrl}/api/${apiRoute}?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${getCookie("_AT")}`,
          },
        }
      );

      setAllData(resp.data.data);
      setTotalCount(resp.data.totalCount);
      setPage(pageOverride ?? page);
      setSortedField(effectiveSortBy);
      setSortDirection(effectiveSortDirection);
      setSearchParams(Object.fromEntries(queryParams.entries()));
    } catch (e) {
      console.error("Fetch error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSort = async (column) => {
    let field = column.field;
    if (field === "concat") {
      field = column.concat[0].subvalue
        ? `${column.concat[0].field}.${column.concat[0].subvalue}`
        : column.concat[0].field;
    }
    const newDirection = sortDirection === "asc" ? "desc" : "asc";

    let currentOverrides = {
      pageOverride: 1,
      sortByOverride: field,
      sortDirectionOverride: newDirection,
    };
    if (column.sortingRelationSubValue != null) {
      currentOverrides.sortingRelationSubValue = column.sortingRelationSubValue;
    }
    if (
      column.sortingRelationWhereField != null &&
      column.sortingRelationWhereValue != null
    ) {
      currentOverrides.sortingRelationWhereField =
        column.sortingRelationWhereField;
      currentOverrides.sortingRelationWhereValue =
        column.sortingRelationWhereValue;
    }
    await fetchData(getCurrentOverrides(currentOverrides));
  };

  const getTextClass = (column, row) =>
    column.values
      ? column.values[
      // When `type: "status"` and `displayValues` are defined, the formatted value
      // used for CSS lookup should still be based on the underlying boolean/string,
      // not the display label. Therefore, we read raw first, then map for display later.
      formatInput(row, column)
      ]
      : "text-green-700 ring-green-600/20 bg-green-50";

  useEffect(() => {
    const updateDivHeight = () => {
      const divElement = document.getElementById("listDiv");
      const currentY = divElement.getBoundingClientRect().top;
      const screenHeight = window.innerHeight;
      const heightDifference = screenHeight - currentY - 60;
      setDivHeight(heightDifference > 0 ? heightDifference : 0);
    };

    updateDivHeight();
    window.addEventListener("resize", updateDivHeight);
    return () => window.removeEventListener("resize", updateDivHeight);
  }, []);
  const getCurrentOverrides = (overrides = {}) => ({
    pageOverride: page,
    searchOverride: searchTerm,
    sortByOverride: sortedField,
    sortDirectionOverride: sortDirection,
    filterByOverride: filterBy.value,
    filterValueOverride: filterValue,
    ...overrides,
  });

  // Generic bulk action selection handlers
  const getItemName = (row) => {
    const nameField = bulkActions?.nameField || "Name";
    return row[nameField] || row.Name || row.name || `Item #${row.id}`;
  };

  const toggleItemSelection = (itemId, itemName) => {
    setSelectedItems((prev) => {
      const exists = prev.find((item) => item.id === itemId);
      if (exists) {
        return prev.filter((item) => item.id !== itemId);
      } else {
        return [...prev, { id: itemId, name: itemName }];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === allData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(
        allData.map((row) => ({
          id: row.id,
          name: getItemName(row),
        }))
      );
    }
  };

  const isItemSelected = (itemId) => {
    return selectedItems.some((item) => item.id === itemId);
  };

  const handleBulkAction = async (businessDateTime, selectedBranchCodes, companies) => {
    if (!bulkActions?.onAction) return;

    setBulkActionProcessing(true);
    try {
      const result = await bulkActions.onAction({
        selectedItems,
        businessDateTime,
        companies
      });

      // Clear selection after action completes
      setSelectedItems([]);
      setShowBulkActionPopup(false);

      // Show success notification using callPopUp
      if (callPopUp && typeof callPopUp === "function") {
        callPopUp({
          isError: false,
          title: "Success",
          description: result?.message || "Bulk action completed successfully",
        });
      }

      // Refresh the data
      await fetchData();

      return result;
    } catch (error) {
      console.error("Error performing bulk action:", error);
      // Clear selection even on failure
      setSelectedItems([]);
      setShowBulkActionPopup(false);

      // Show error notification using callPopUp
      if (callPopUp && typeof callPopUp === "function") {
        callPopUp({
          isError: true,
          title: "Error",
          description: error?.message || "Failed to complete bulk action",
        });
      }
    } finally {
      setBulkActionProcessing(false);
    }
  };

  const handleCSVExport = async (exportData) => {
    try {
      // Prepare the API call data
      const apiData = {
        fields: exportData.fields.map((field) => ({
          field: field.field,
          label: field.label,
          concat: field.concat,
          subvalue: field.subvalue,
          isRelation: field.isRelation,
          relationWith: field.relationWith,
          where: field.where,
          isArray: field.isArray,
          showValue: field.showValue,
          isDate: field.isDate,
          branchCode: field.branchCode,
        })),
        filters: activeFilters,
        searchTerm: searchTerm,
        sortBy: sortedField,
        sortDirection: sortDirection,
        timestamp: exportData.timestamp,
        currentTable: tableNameInDb,
      };

      // Example API call - replace with your actual export endpoint
      const response = await axios.post(
        `${config.apiUrl}/api/exports/export-csv`,
        apiData,
        {
          headers: {
            Authorization: `Bearer ${getCookie("_AT")}`,
            "Content-Type": "application/json",
          },
          responseType: "blob", // Important for file downloads
        }
      );

      // Create and trigger download
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title || "data"}-export-${new Date().toISOString().split("T")[0]
        }.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Export failed. Please try again.");
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div
            id="listDiv"
            style={{ height: `${divHeight}px` }}
            className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8"
          >
            <div className="flex flex-col gap-4 mb-2">
              {/* Search Row */}
              <div className="flex flex-col md:flex-row items-center gap-4">
                <form
                  className="flex gap-4 w-full md:w-[300px]"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    await fetchData({
                      pageOverride: 1,
                      searchOverride: searchTerm,
                    });
                  }}
                >
                  <div className="flex gap-2 items-center w-full">
                    <input
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="focus:ring-primary block w-full rounded-md border-0 py-1.5 pr-10 ring-1 ring-inset ring-primary/30 sm:text-sm"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => {
                          fetchData({ pageOverride: 1, searchOverride: "" });
                          setSearchTerm("");
                        }}
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                  <button type="submit">
                    <MagnifyingGlassIcon className="h-5 w-5" />
                  </button>
                </form>

                <div className="w-7">
                  {isLoading && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="animate-spin h-5 w-5"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="#0167ff"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Filter Row */}
              <div className="flex flex-wrap items-center gap-2">
                <SearchableDropdownOptions
                  options={columns
                    .filter((c) => c.showInFilter)
                    .map((c) => ({
                      value: c.label,
                      label: c.label,
                      field: c.field,
                      concat: c.concat || null,
                      subvalue: c.subvalue || null,
                      isDate: c.isDate || null,
                      relationWith: c.relationWith || null,
                      isRelation: c.isRelation || null,
                    }))}
                  value={filterBy.name}
                  onChange={async (e) => {

                    if (!e) {
                      setFilterBy({ name: "", value: "" });
                      setStatusOptions([]);
                      return;
                    }

                    const selectedValue =
                      window.location.pathname === "/vehicles" &&
                        e.value === "Category"
                        ? "class"
                        : window.location.pathname === "/vehiclerates" &&
                          e.value === "Vehicle Category"
                          ? "vehicle_class"
                          : e.field;

                    let filterUrl = `${config.apiUrl}/api/common/filtervalues?filterBy=${selectedValue}&tableNameInDb=${tableNameInDb}`;
                    let filterPath = selectedValue;

                    if (e.subvalue) {
                      filterPath = `${selectedValue.toLowerCase()}.${e.subvalue
                        }`;
                      filterUrl += `&concat=${filterPath}`;
                    }

                    if (Array.isArray(e.concat) && e.concat.length > 0) {
                      filterPath = e.concat
                        .map((c) => `${c.field}.${c.subvalue}`)
                        .join(",");
                      filterUrl += `&concat=${filterPath}`;
                    }

                    setFilterBy({ name: e.value, value: filterPath });
                    if (e.isRelation === true && e.relationWith != null) {
                      filterUrl += `&relationWith=${e.relationWith}`;
                    }
                    const response = await axios.get(filterUrl, {
                      headers: {
                        Authorization: `Bearer ${getCookie("_AT")}`,
                      },
                    });

                    const items = Array.isArray(response?.data?.data)
                      ? response.data.data
                      : [];

                    // If selected filter is a boolean-like column (e.g. Sequence),
                    // normalize options to include both true/false consistently.
                    const booleanLike = items.every(
                      (v) => typeof v === "boolean"
                    );
                    let normalized = items;
                    if (booleanLike) {
                      const hasTrue = items.includes(true);
                      const hasFalse = items.includes(false);
                      if (!hasTrue || !hasFalse) {
                        normalized = [true, false];
                      }
                    } else {
                      // If some items are boolean but not all returned (e.g., only true), add false
                      const someBoolean = items.some(
                        (v) => typeof v === "boolean"
                      );
                      if (someBoolean && !items.includes(false)) {
                        normalized = [...items, false];
                      }
                    }

                    // De-dupe
                    const seen = new Set();
                    const unique = normalized.filter((v) => {
                      const key = `${typeof v}:${String(v)}`;
                      if (seen.has(key)) return false;
                      seen.add(key);
                      return true;
                    });

                    // Map labels; if this column defines displayValues, use them for booleans
                    const columnMeta = columns.find(
                      (c) => c.label === e.value
                    );
                    const displayMap = columnMeta?.displayValues || null;

                    setStatusOptions(
                      unique.map((item) => {
                        if (typeof item === "boolean") {
                          const key = item ? "True" : "False";
                          const label = displayMap?.[key] || key;
                          return { value: item, label };
                        }
                        return { value: item, label: String(item) };
                      })
                    );

                    setFilterValue("");
                  }}
                  placeholder="Filter By"
                />

                {filterBy.name &&
                  columns.find((c) => c.label === filterBy.name)?.isDate ? (
                  <>
                    <SearchableDropdownOptions
                      options={[
                        { label: "On or After", value: "_gte" },
                        { label: "On or Before", value: "_lte" },
                      ]}
                      value={dateComparisonOperator}
                      onChange={(e) => setDateComparisonOperator(e.value)}
                      placeholder="Comparison"
                    />
                    <div className="min-w-[200px]">
                      <InputDateTimePickerMUI
                        allowTime={false}
                        name="filter_date"
                        defaultValue={filterValue}
                        handleChange={(e) => setFilterValue(e.target.value)}
                        isClearable={true}
                        wrapperStyle={{
                          marginTop: 0,
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <SearchableDropdownOptions
                    options={statusOptions}
                    value={filterValue}
                    onChange={(e) =>
                      setFilterValue(
                        // Preserve boolean values for backend filtering; otherwise pass through string value
                        typeof e?.value === "boolean" ? e.value : e?.value
                      )
                    }
                    placeholder="Select Value"
                    disabled={!filterBy.name}
                    isLoading={isLoadingOptions}
                  />
                )}

                <SearchableDropdownOptions
                  options={[
                    { label: "AND", value: "_and" },
                    { label: "OR", value: "_or" },
                  ]}
                  value={filterOperator}
                  onChange={(e) => setFilterOperator(e?.value || "_and")}
                  placeholder="Operator"
                />

                <button
                  type="button"
                  onClick={async () => {
                    if (filterBy.value != null && filterValue != null) {
                      const columnMeta = columns.find(
                        (c) => c.label === filterBy.name
                      );
                      const isDate = columnMeta?.isDate;

                      const field = filterBy.value;
                      let value;
                      if (isDate) {
                        // For inclusive date filtering, ensure we get the full day
                        let dateForFilter = filterValue;
                        if (dateComparisonOperator === "_gte") {
                          // "From" - start of day, convert to UTC
                          const localDate = new Date(filterValue + "T00:00:00");
                          dateForFilter = localDate.toISOString();
                        } else if (dateComparisonOperator === "_lte") {
                          // "To" - end of day, convert to UTC
                          const localDate = new Date(filterValue + "T23:59:59");
                          dateForFilter = localDate.toISOString();
                        }
                        value = { [dateComparisonOperator]: dateForFilter };
                      } else {
                        value = filterValue;
                      }

                      const updatedFilters = [
                        ...activeFilters,
                        columnMeta.isRelation === true &&
                          columnMeta.relationSubvalue != null
                          ? {
                            field,
                            value,
                            subValue: columnMeta.relationSubvalue,
                            isRelation: columnMeta.isRelation,
                            operator: filterOperator || "_and",
                          }
                          : {
                            field,
                            value,
                            operator: filterOperator || "_and",
                          },
                      ];

                      setActiveFilters(updatedFilters);

                      await fetchData({
                        pageOverride: 1,
                        filtersOverride: updatedFilters,
                      });
                    }
                  }}
                  className="text-sm bg-primary text-white px-3 py-1 rounded hover:bg-primary/80 transition"
                >
                  Add
                </button>

                <button
                  type="button"
                  className="text-sm text-primary hover:underline ml-2"
                  onClick={async () => {
                    const clearedFilters = [];

                    // Clear UI state
                    setSearchTerm("");
                    setFilterBy({ name: "", value: "" });
                    setFilterValue("");
                    setSortedField(null);
                    setSortDirection("asc");
                    setPage(1);
                    setActiveFilters(clearedFilters);
                    await fetchData({
                      pageOverride: 1,
                      searchOverride: "",
                      sortByOverride: null,
                      sortDirectionOverride: "asc",
                      filtersOverride: clearedFilters,
                    });
                    setSearchParams({});
                  }}
                >
                  Clear All
                </button>
                <button
                  type="button"
                  className="text-sm text-primary hover:underline ml-2"
                  onClick={() => setIsExportModalOpen(true)}
                >
                  Export CSV
                </button>
                {hasImport && (
                  <button
                    type="button"
                    className="text-sm text-primary hover:underline ml-2"
                    onClick={() => setIsImportModalOpen(true)}
                  >
                    Import CSV
                  </button>
                )}

                {/* Bulk Action Button - Simple inline button */}
                {bulkActionsEnabled && selectedItems.length > 0 && (
                  <button
                    type="button"
                    className="inline-flex items-center px-3 py-1 border border-indigo-600 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-700 text-sm font-medium rounded-md ml-2 transition-colors"
                    onClick={() => setShowBulkActionPopup(true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12H12m-8.25 5.25h16.5" />
                    </svg>
                    {bulkActions.buttonLabel || "Action"} [{selectedItems.length}]
                  </button>
                )}

                {/* Record Count Indicator - Show for all pages - Responsive positioning */}
                <div className="text-sm text-gray-600 xl:ml-auto">
                  {(() => {
                    if (Array.isArray(allData) === false) return "";
                    const getEntityName = (pathname) => {
                      if (pathname === "/vehicles") return "vehicles";
                      else if (pathname === "/customers") return "customers";
                      else if (pathname === "/reservations")
                        return "reservations";
                      else if (pathname === "/companies") return "companies";
                      else if (pathname === "/cities") return "cities";
                      else return "records"; // fallback for any other pages
                    };

                    const entityName = getEntityName(window.location.pathname);

                    if (
                      Array.isArray(allData) === true &&
                      (totalCount === 0 || allData.length === 0)
                    ) {
                      return `Showing 0 of 0 ${entityName}`;
                    }

                    // Backend uses 50 items per page (limitPerPage = 50)
                    const itemsPerPage = 50;
                    const firstRecord = (page - 1) * itemsPerPage + 1;
                    const lastRecord = Math.min(
                      totalCount,
                      (page - 1) * itemsPerPage + allData.length
                    );

                    return `Showing ${firstRecord}-${lastRecord} of ${totalCount} ${entityName}`;
                  })()}
                </div>
              </div>

              {/* Active Filters */}
              {activeFilters.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {activeFilters.map((f, index) => {
                    // Helper function to get human-readable field name
                    const getFieldLabel = (fieldPath) => {
                      let column = columns.find((c) => {
                        if (c.field === fieldPath) return true;
                        if (c.concat) {
                          const concatPath = c.concat
                            .map((col) =>
                              col.subvalue
                                ? `${col.field}.${col.subvalue}`
                                : col.field
                            )
                            .join(",");
                          return concatPath === fieldPath;
                        }
                        return false;
                      });
                      // If not found, try matching the base field (before the dot)
                      if (!column && fieldPath.includes(".")) {
                        const baseField = fieldPath.split(".")[0];
                        column = columns.find((c) => c.field === baseField);
                      }
                      return column?.label || fieldPath;
                    };

                    // Helper function to convert operators to human text
                    const getOperatorText = (operator) => {
                      const operatorMap = {
                        _gte: "On or After",
                        _lte: "On or Before",
                        _gt: "After",
                        _lt: "Before",
                        _eq: "Equals",
                        _neq: "Not Equals",
                        _contains: "Contains",
                        _in: "In",
                      };
                      return operatorMap[operator] || operator;
                    };

                    // Helper function to format date values and use Filter By dropdown label for value
                    const formatValue = (value, field) => {
                      if (typeof value === "object" && !Array.isArray(value)) {
                        const operator = Object.keys(value)[0];
                        const val = Object.values(value)[0];
                        // Check if it's a date (ISO string)
                        if (
                          typeof val === "string" &&
                          val.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)
                        ) {
                          const date = new Date(val);
                          // Force d/m/y format
                          const formattedDate = `${date.getDate()}/${date.getMonth() + 1
                            }/${date.getFullYear()}`;
                          return `${getOperatorText(
                            operator
                          )} ${formattedDate}`;
                        }
                        // Try to find a label from statusOptions (Filter By dropdown)
                        let label = val;
                        const dropdownOption = statusOptions?.find(
                          (opt) => opt.value === val
                        );
                        if (dropdownOption) label = dropdownOption.label;
                        return `${getOperatorText(operator)} ${label}`;
                      }
                      // Try to find a label from statusOptions (Filter By dropdown)
                      let label = value;
                      const dropdownOption = statusOptions?.find(
                        (opt) => opt.value === value
                      );
                      if (dropdownOption) label = dropdownOption.label;

                      // React does not render booleans as text; ensure booleans
                      // (e.g. Sequence true/false) are converted to a string.
                      if (typeof label === "boolean") {
                        return label ? "True" : "False";
                      }

                      return label;
                    };

                    return (
                      <span
                        key={`${f.field}-${f.value}-${index}`}
                        className="bg-gray-200 text-sm px-2 py-1 rounded-full flex items-center gap-2"
                      >
                        <span className="text-primary font-semibold px-1">
                          {f.operator === "_or" ? "OR" : "AND"}
                        </span>
                        <span className="text-gray-800">
                          {getFieldLabel(f.field)}:{" "}
                          {formatValue(f.value, f.field)}
                        </span>
                        <button
                          type="button"
                          onClick={async () => {
                            const newFilters = activeFilters.filter(
                              (_, i) => i !== index
                            );
                            setActiveFilters(newFilters);
                            await fetchData({
                              pageOverride: 1,
                              filtersOverride: newFilters,
                            });
                          }}
                        >
                          <XMarkIcon className="w-4 h-4 text-gray-600" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  {/* Checkbox column header - Only when bulk actions enabled */}
                  {bulkActionsEnabled && (
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0 w-10">
                      <input
                        type="checkbox"
                        checked={allData?.length > 0 && selectedItems.length === allData.length}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                        title="Select all"
                      />
                    </th>
                  )}
                  {columns.map((column, index) => {
                    if (
                      column != null &&
                      column.hide != null &&
                      column.hide === true
                    )
                      return;
                    return (
                      <th
                        key={column.field + "_" + index}
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                      >
                        <button
                          href="#"
                          style={{
                            whiteSpace: "nowrap",
                          }}
                          className="group inline-flex"
                          onClick={() => {
                            if (
                              column.allowSorting != null &&
                              column.allowSorting === true
                            ) {
                              toggleSort(column);
                            }
                          }}
                        >
                          {column.label}
                          {column.allowSorting && (
                            <span className="ml-2 flex-none rounded text-gray-400 self-center">
                              {column.field === "concat" ? (
                                sortedField ===
                                  `${column.concat[0].field}.${column.concat[0].subvalue}` ? (
                                  sortDirection === "asc" ? (
                                    <ChevronUpIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <ChevronDownIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  )
                                ) : (
                                  <ChevronUpDownIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                )
                              ) : sortedField === column.field &&
                                ((column.sortingRelationSubValue == null &&
                                  !searchParams.get(
                                    "sortingRelationSubValue"
                                  )) ||
                                  (column.sortingRelationSubValue != null &&
                                    searchParams.get(
                                      "sortingRelationSubValue"
                                    ) === `${column.sortingRelationSubValue}` &&
                                    (column.sortingRelationWhereField == null ||
                                      (searchParams.get(
                                        "sortingRelationWhereField"
                                      ) ===
                                        `${column.sortingRelationWhereField}` &&
                                        searchParams.get(
                                          "sortingRelationWhereValue"
                                        ) ===
                                        `${column.sortingRelationWhereValue}`)))) ? (
                                sortDirection === "asc" ? (
                                  <ChevronUpIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <ChevronDownIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                )
                              ) : (
                                <ChevronUpDownIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              )}
                            </span>
                          )}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {allData?.length > 0 &&
                  allData.map((row) => {
                    return (
                      <tr
                        key={row.id}
                        className={`hover:bg-[#fafafa] ${bulkActionsEnabled && isItemSelected(row.id) ? 'bg-indigo-50' : ''}`}
                        style={{
                          cursor: editRoles?.includes(currentRole)
                            ? "pointer"
                            : "not-allowed",
                        }}
                        onClick={(e) => {
                          // Don't navigate if clicking on checkbox
                          if (e.target.type === 'checkbox') return;

                          const _e = {
                            target: {
                              name: row.id,
                            },
                          };
                          if (editRoles?.includes(currentRole)) {
                            navigate(`${row.id}`, {
                              state: { from: location },
                            });
                            // editClick(_e);
                          }
                        }}
                      >
                        {/* Checkbox cell - Only when bulk actions enabled */}
                        {bulkActionsEnabled && (
                          <td className="py-4 pl-4 pr-3 text-sm sm:pl-0 w-10">
                            <input
                              type="checkbox"
                              checked={isItemSelected(row.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleItemSelection(row.id, getItemName(row));
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                            />
                          </td>
                        )}
                        {columns.map((column, index) => {
                          if (
                            column != null &&
                            column.hide != null &&
                            column.hide === true
                          )
                            return;
                          // console.log(formatInput(row, column));
                          const branchCode = row.branch?.code;
                          // console.log(row, column);

                          return (
                            <td
                              key={column.field + "_" + index}
                              className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0"
                            >
                              {!column.type &&
                                !column.imageField &&
                                column.field !== "concat" &&
                                formatInput(
                                  row,
                                  column,
                                  undefined,
                                  undefined,
                                  branchCode != null ? branchCode : undefined
                                )}

                              {!column.type &&
                                column.imageField &&
                                column.field !== "concat" && (
                                  <div className="flex items-center">
                                    <div className="h-11 w-11 flex-shrink-0">
                                      <div>
                                        <img
                                          className="h-11 w-11 rounded-full"
                                          src={`${config.apiUrl}/assets/${row[column.imageField.name]
                                            }`}
                                          alt=""
                                        />
                                      </div>
                                    </div>
                                    <div className="ml-4">
                                      <div className="">
                                        {formatInput(row, column)}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              {column.imageField &&
                                column.field === "concat" &&
                                column.concat &&
                                Array.isArray(column.concat) && (
                                  <div className="flex items-center">
                                    <div className="h-11 w-11 flex-shrink-0 align-center flex items-center">
                                      <img
                                        className="w-full h-full rounded-full"
                                        src={`${config.apiUrl}/assets/${row[column.imageField]?.name
                                          }`}
                                        alt=""
                                      />
                                    </div>
                                    <div className="ml-4">
                                      <div className="">
                                        {column.concat.map(
                                          (col, i, arrr) =>
                                            formatInput(row, col) +
                                            (i + 1 !== arrr.length
                                              ? column.concatBy ?? " "
                                              : "")
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              {column.type === "color" && (
                                <>
                                  {" "}
                                  <div
                                    className="w-4 h-4 mr-4 rounded-full cursor-pointer inline-block align-sub"
                                    style={{
                                      backgroundColor: formatInput(row, column),
                                    }}
                                  ></div>
                                  <div className="inline-block">
                                    {!column.showValue
                                      ? formatInput(row, column)
                                      : formatInput(row, column, true, false)}
                                  </div>
                                </>
                              )}
                              {!column.imageField &&
                                column.field === "concat" &&
                                column.concat &&
                                Array.isArray(column.concat) &&
                                column.concat.map(
                                  (col, i, arrr) =>
                                    formatInput(row, col) +
                                    (i + 1 !== arrr.length
                                      ? column.concatBy ?? " "
                                      : "")
                                )}
                              {column.type === "status" && (
                                <span
                                  className={classNames(
                                    `inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset`,
                                    getTextClass(column, row)
                                  )}
                                >
                                  {(() => {
                                    const raw = formatInput(row, column);
                                    // If a display mapping is provided (e.g., for booleans), apply it
                                    if (column.displayValues && raw != null) {
                                      // raw can be "True"/"False" for booleans due to removeTrailingZeros
                                      const key = String(raw);
                                      const mapped = column.displayValues[key];
                                      if (mapped != null) return mapped;
                                    }
                                    return raw;
                                  })()}
                                </span>
                              )}
                              {column.type === "condition" &&
                                column.trueCondition &&
                                column.elseCondition
                                ? formatInput(row, column) === column.trueValue
                                  ? column.trueCondition.field === "concat"
                                    ? column.trueCondition.concat &&
                                      Array.isArray(column.trueCondition.concat)
                                      ? column.trueCondition.concat.map(
                                        (col, i, arrr) =>
                                          formatInput(row, col) +
                                          (i + 1 !== arrr.length
                                            ? column.trueCondition.concatBy ??
                                            " "
                                            : "")
                                      )
                                      : ""
                                    : formatInput(row, column.trueCondition)
                                  : column.elseCondition.field === "concat"
                                    ? column.elseCondition.concat &&
                                      Array.isArray(column.elseCondition.concat)
                                      ? column.elseCondition.concat.map(
                                        (col, i, arrr) =>
                                          formatInput(row, col) +
                                          (i + 1 !== arrr.length
                                            ? column.elseCondition.concatBy ??
                                            " "
                                            : "")
                                      )
                                      : ""
                                    : formatInput(row, column.elseCondition)
                                : ""}
                            </td>
                          );
                        })}
                        <td className="justify-end relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm sm:pr-0 flex">
                          <a
                            href={`${window.location.pathname}/${row.id}`}
                            target="_blank"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              className="ml-3 h-5 w-5 align-sub text-gray-500 hover:text-red-900"
                            >
                              <path
                                stroke="none"
                                d="M0 0h24v24H0z"
                                fill="none"
                              />
                              <path d="M3 16m0 1a1 1 0 0 1 1 -1h3a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1z" />
                              <path d="M4 12v-6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-6" />
                              <path d="M12 8h4v4" />
                              <path d="M16 8l-5 5" />
                            </svg>
                          </a>
                          {deleteRoles?.includes(currentRole) && (
                            <button
                              name={row.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                duplicateClicked(row.id);
                              }}
                              className="ml-3 h-5 w-5 align-sub text-gray-500 hover:text-red-900"
                            >
                              <DocumentDuplicateIcon />
                            </button>
                          )}
                          {deleteRoles?.includes(currentRole) && (
                            <button
                              name={row.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  (process.env.REACT_APP_MANAGER_ROLE ===
                                    currentRole ||
                                    process.env.REACT_APP_FINANCE_ROLE ===
                                    currentRole) &&
                                  row.status !== "Draft" &&
                                  window.location.pathname.endsWith(
                                    "/reservations"
                                  )
                                )
                                  return;
                                deleteClicked(row.id);
                              }}
                              style={{
                                opacity:
                                  (process.env.REACT_APP_MANAGER_ROLE ===
                                    currentRole ||
                                    process.env.REACT_APP_FINANCE_ROLE ===
                                    currentRole) &&
                                    row.status !== "Draft" &&
                                    window.location.pathname.endsWith(
                                      "/reservations"
                                    )
                                    ? "50%"
                                    : "unset",
                                cursor:
                                  (process.env.REACT_APP_MANAGER_ROLE ===
                                    currentRole ||
                                    process.env.REACT_APP_FINANCE_ROLE ===
                                    currentRole) &&
                                    row.status !== "Draft" &&
                                    window.location.pathname.endsWith(
                                      "/reservations"
                                    )
                                    ? "not-allowed"
                                    : "pointer",
                              }}
                              className="ml-3 h-5 w-5 align-sub text-red-600 hover:text-red-900"
                            >
                              <TrashIcon name={row.id} />
                              <span className="sr-only">Delete-{row.id}</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>

        <PaginationButtons
          page={page}
          totalCount={totalCount}
          nextButtonOnClick={async () =>
            await fetchData(getCurrentOverrides({ pageOverride: page + 1 }))
          }
          backButtonOnClick={async () =>
            await fetchData(getCurrentOverrides({ pageOverride: page - 1 }))
          }
        />
      </div>
      <ExportCSVModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        columns={columns}
        onExport={handleCSVExport}
      />
      {hasImport && (
        <ImportCSVModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          columns={columns}
          tableName={tableNameInDb}
          relationshipMappings={importConfig?.relationshipMappings || []}
          onImportComplete={() => {
            setIsImportModalOpen(false);
            fetchData();
          }}
        />
      )}

      {/* Bulk Action Popup - Using existing SequenceConfirmationPopup */}
      {showBulkActionPopup && bulkActionsEnabled && (
        <SequenceConfirmationPopup
          companies={selectedItems}
          onConfirm={handleBulkAction}
          onCancel={() => setShowBulkActionPopup(false)}
          sequencing={bulkActionProcessing}
          isBulkCompanyBilling={true}
        />
      )}
    </div>
  );
}
